import { QUALITY_PRESETS } from '@cloudpearl/config';
import type { QualityLevel, QualityPreset } from '@cloudpearl/config';

/**
 * Pick a starting tier from coarse device signals. Dynamic resolution then fine-tunes at runtime,
 * so this only needs to be roughly right.
 */
let detected: QualityLevel | null = null;

export function detectQuality(): QualityLevel {
  detected ??= detect();
  return detected;
}

function detect(): QualityLevel {
  const touch = matchMedia('(pointer: coarse)').matches;
  const small = Math.min(screen.width, screen.height) < 820;
  const cores = navigator.hardwareConcurrency ?? 4;
  const mem = (navigator as unknown as { deviceMemory?: number }).deviceMemory ?? 8;
  let gpu = '';
  try {
    const c = document.createElement('canvas');
    const gl = c.getContext('webgl2');
    const ext = gl?.getExtension('WEBGL_debug_renderer_info');
    if (gl && ext) gpu = String(gl.getParameter(ext.UNMASKED_RENDERER_WEBGL)).toLowerCase();
    gl?.getExtension('WEBGL_lose_context')?.loseContext();
  } catch {
    /* ignore */
  }
  const weakGpu = /(intel\(r\) hd|intel hd|uhd graphics 6|mali-g5|mali-t|adreno \(tm\) [3-5]|powervr|swiftshader|llvmpipe)/.test(gpu);
  const strongGpu = /(apple m\d|rtx|radeon rx|radeon pro|geforce gtx 1[06-9]|apple gpu)/.test(gpu);
  // Phones and tablets: thermals, not peak speed, set the budget — never above medium.
  if (touch) {
    if (small && (weakGpu || mem <= 3 || cores <= 4)) return 'low';
    return 'medium';
  }
  // Integrated Intel graphics (HD / UHD / Iris) are fill-rate bound at laptop resolutions.
  if (weakGpu || /intel/.test(gpu)) return 'medium';
  if (strongGpu || cores >= 8) return 'high';
  return 'medium';
}

export function presetFor(setting: 'auto' | QualityLevel): QualityPreset {
  return QUALITY_PRESETS[setting === 'auto' ? detectQuality() : setting];
}
